import { useState } from 'react'
import StepBadge from './StepBadge'
import JsonViewer from './JsonViewer'

export interface ExtractionData {
  employee?: {
    employee_id?: string | null
    employee_name?: string | null
    department?: string | null
    position?: string | null
    supervisor?: string | null
    [key: string]: unknown
  }
  time_period?: {
    start_date?: string | null
    end_date?: string | null
    pay_period_number?: string | null
    week_number?: string | null
    [key: string]: unknown
  }
  entries?: Array<{
    date?: string
    day_of_week?: string | null
    start?: string | null
    end?: string | null
    break_minutes?: number | null
    regular_hours?: number | null
    overtime_hours?: number | null
    job_code?: string | null
    cost_center?: string | null
    notes?: string | null
    total_hours?: number | null
    [key: string]: unknown
  }>
  totals?: {
    regular_hours?: number | null
    overtime_hours?: number | null
    total_hours?: number | null
    [key: string]: unknown
  }
  total_hours?: number | null
  entry_count?: number
  confidence?: number
  warnings?: string[]
  [key: string]: unknown
}

function formatDateRange(start?: string | null, end?: string | null): string {
  if (!start && !end) return '—'
  const fmt = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  if (start && end && start !== end) return `${fmt(start)} – ${fmt(end)}`
  return fmt((start ?? end)!)
}

interface ExtractingStateProps { message?: string }
function ExtractingState({ message = 'Analyzing your time card' }: ExtractingStateProps) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-12 text-center min-h-[300px]">
      <svg className="w-8 h-8 text-brand animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <div>
        <p className="text-gray-900 text-sm font-medium">Extracting data…</p>
        <p className="text-gray-500 text-xs mt-1">{message}</p>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-12 text-center min-h-[300px]">
      <div className="w-14 h-14 rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center">
        <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
      </div>
      <div>
        <p className="text-gray-500 text-sm font-medium">No extraction yet</p>
        <p className="text-gray-400 text-xs mt-1">Upload a file — extraction starts automatically.</p>
      </div>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-12 text-center min-h-[300px]">
      <div className="w-14 h-14 rounded-2xl border-2 border-dashed border-red-300 flex items-center justify-center">
        <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
        </svg>
      </div>
      <div>
        <p className="text-red-500 text-sm font-medium">Extraction failed</p>
        <p className="text-gray-500 text-xs mt-1 max-w-xs">{message}</p>
      </div>
    </div>
  )
}

function ExtractionSummary({ data }: { data: ExtractionData }) {
  const name = data.employee?.employee_name
  const entryCount = data.entry_count ?? data.entries?.length
  const totalHours = data.total_hours ?? data.totals?.total_hours
  const dateRange = formatDateRange(data.time_period?.start_date, data.time_period?.end_date)

  return (
    <div className="px-6 py-4">
      <p className="text-gray-400 text-[10px] uppercase tracking-wider font-medium mb-2">Summary</p>
      <p className="text-gray-700 text-sm leading-relaxed">
        {entryCount != null && (
          <>
            Extracted{' '}
            <span className="text-gray-900 font-medium">{entryCount} {entryCount === 1 ? 'entry' : 'entries'}</span>
            {name && <> for <span className="text-gray-900 font-medium">{name}</span></>}.
          </>
        )}
        {totalHours != null && (
          <>
            {' '}Total hours: <span className="text-gray-900 font-medium">{totalHours}</span>
          </>
        )}
        {data.time_period && (data.time_period.start_date || data.time_period.end_date) && (
          <> &mdash; <span className="text-gray-900 font-medium">{dateRange}</span>.</>
        )}
        {!entryCount && !totalHours && !data.time_period && (
          <span className="text-gray-500">Data extracted. Review the JSON output below.</span>
        )}
      </p>
    </div>
  )
}

interface JsonOutputPanelProps {
  json: string
  copied: boolean
  onCopy: () => void
  onDownload: () => void
}

function JsonOutputPanel({ json, copied, onCopy, onDownload }: JsonOutputPanelProps) {
  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-3 shrink-0">
        <p className="text-gray-400 text-[10px] uppercase tracking-wider font-medium">JSON Output</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCopy}
            className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 px-2.5 py-1 text-xs text-gray-600 transition-colors cursor-pointer"
          >
            {copied ? (
              <>
                <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                </svg>
                Copy JSON
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onDownload}
            className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 px-2.5 py-1 text-xs text-gray-600 transition-colors cursor-pointer"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Download .JSON
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-gray-50 px-4 pb-4 min-h-[200px] max-h-[340px]">
        <JsonViewer json={json} />
      </div>
    </div>
  )
}

function ConfidenceWarnings({ data }: { data: ExtractionData }) {
  const confidence = data.confidence ?? 1
  const warnings = data.warnings ?? []
  const pct = Math.round(confidence * 100)
  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'

  return (
    <div className="px-6 py-4 shrink-0">
      <p className="text-gray-400 text-[10px] uppercase tracking-wider font-medium mb-3">Confidence &amp; Warnings</p>
      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-2 flex-1">
          <span className="text-gray-700 text-sm font-medium tabular-nums shrink-0">{pct}%</span>
          <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        {warnings.length > 0 && (
          <div className="flex items-center gap-1.5 shrink-0">
            <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
            <span className="text-amber-500 text-sm font-medium">{warnings.length}</span>
          </div>
        )}
      </div>
      {warnings.length > 0 && (
        <div className="space-y-1.5">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              <svg className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
              <p className="text-amber-700 text-xs">{w}</p>
            </div>
          ))}
        </div>
      )}
      {warnings.length === 0 && (
        <div className="flex items-center gap-2 text-emerald-600 text-xs">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
          No warnings — all fields extracted cleanly.
        </div>
      )}
    </div>
  )
}

interface Props {
  extractedData: ExtractionData | null
  extracting: boolean
  error?: string | null
}

export default function ExtractionResultCard({ extractedData, extracting, error }: Props) {
  const [copied, setCopied] = useState(false)

  const json = extractedData ? JSON.stringify(extractedData, null, 2) : ''

  function handleCopy() {
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleDownload() {
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'time-card-extraction.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl flex flex-col overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
        <StepBadge n={2} />
        <h2 className="text-gray-900 font-semibold text-base">Extraction Result</h2>
      </div>

      {extracting && <ExtractingState />}

      {!extracting && error && <ErrorState message={error} />}

      {!extracting && !error && !extractedData && <EmptyState />}

      {!extracting && !error && extractedData && (
        <div className="flex-1 flex flex-col divide-y divide-gray-200 overflow-hidden">
          <ExtractionSummary data={extractedData} />
          <JsonOutputPanel json={json} copied={copied} onCopy={handleCopy} onDownload={handleDownload} />
          <ConfidenceWarnings data={extractedData} />
        </div>
      )}
    </div>
  )
}
